import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation, Navigate } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { Landmark } from "lucide-react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Router state is passed by CheckoutPage on the initial navigate(). If the
// user reloads the page (state is lost), we fall back to a sessionStorage
// snapshot written at the same time.
function readStoredTransfer(orderNumber) {
  if (!orderNumber || typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(`csm_transfer_${orderNumber}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function TransferPage() {
  const { t, lang } = useLang();
  const loc = useLocation();
  const orderNumber = new URLSearchParams(loc.search).get("order_number");
  const emailParam = new URLSearchParams(loc.search).get("email");

  // Router state is authoritative when present; sessionStorage covers reloads.
  const initialSnapshot = useMemo(
    () => loc.state || readStoredTransfer(orderNumber) || null,
    [loc.state, orderNumber]
  );
  const [snapshot] = useState(initialSnapshot);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderNumber || !emailParam) return;
    axios
      .get(`${API}/orders/by-number/${orderNumber}`, { params: { email: emailParam } })
      .then((res) => setOrder(res.data))
      .catch(() => null);
  }, [orderNumber, emailParam]);

  // Block all transfer/purchase flows in pre-harvest mode (after all hooks)
  if (PRE_HARVEST_MODE) {
    return <Navigate to={lang === "en" ? "/en" : "/"} replace />;
  }


  const bank = snapshot?.bank || {
    beneficiary: "Cortijo Santa María",
    iban: "",
    bic: "",
    concept: orderNumber || "",
  };
  const total = snapshot?.total ?? order?.total ?? null;
  const euro = (n) => (n == null ? "—" : (n || 0).toFixed(2).replace(".", ",") + " €");

  return (
    <main data-testid="transfer-page" className="min-h-screen bg-cream-100 text-charcoal">
      <ShopNav />
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
        <Landmark size={48} strokeWidth={1.2} className="text-gold-deep mx-auto" />
        <h1 className="mt-8 font-serif text-4xl sm:text-5xl text-olive-900 text-center">
          {t("transfer_page.title")}
        </h1>
        <p className="mt-4 text-olive-800/80 font-light text-center">
          {t("transfer_page.subtitle")}
        </p>

        <div className="mt-10 bg-cream-200 border border-cream-400 p-6 sm:p-10">
          <dl className="grid sm:grid-cols-2 gap-y-4 gap-x-8">
            {orderNumber && (
              <>
                <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
                  {t("success_page.order_number")}
                </dt>
                <dd
                  data-testid="transfer-order-number"
                  className="font-serif text-olive-900 text-lg"
                >
                  {orderNumber}
                </dd>
                <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
                  {t("transfer_page.amount")}
                </dt>
                <dd
                  data-testid="transfer-amount"
                  className="font-serif text-olive-900 text-lg"
                >
                  {euro(total)}
                </dd>
              </>
            )}
            <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
              {t("transfer_page.beneficiary")}
            </dt>
            <dd
              data-testid="transfer-beneficiary"
              className="font-serif text-olive-900 text-lg"
            >
              {bank.beneficiary}
            </dd>
            <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
              {t("transfer_page.iban")}
            </dt>
            <dd
              data-testid="transfer-iban"
              className="font-serif text-olive-900 text-lg break-all"
            >
              {bank.iban || "—"}
            </dd>
            {bank.bic ? (
              <>
                <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
                  BIC / SWIFT
                </dt>
                <dd
                  data-testid="transfer-bic"
                  className="font-serif text-olive-900 text-lg"
                >
                  {bank.bic}
                </dd>
              </>
            ) : null}
            <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
              {t("transfer_page.concept")}
            </dt>
            <dd
              data-testid="transfer-concept"
              className="font-serif text-olive-900 text-lg"
            >
              {bank.concept || orderNumber || "—"}
            </dd>
          </dl>
          <p className="mt-8 pt-6 border-t border-cream-400 text-sm text-olive-800/75 font-light">
            {t("transfer_page.note")}
          </p>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/"
            className="px-6 py-3 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.26em] hover:bg-cream-200"
          >
            {t("success_page.back_home")}
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
}
